import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'

// decisionType is intentionally fixed to OK in operator UI (safety policy).
const MATCH_TYPES = ['DX_EXACT', 'DX_PREFIX', 'DX_RANGE3', 'NAME_CONTAINS']

function normalizeCode(v) {
  return (v || '').toString().trim().toUpperCase()
}

function emptyRule() {
  return {
    id: null,
    insurerCode: '',
    templateId: '',
    conditionKey: '',
    decisionType: 'OK',
    waitingDays: '',
    maxCount: '',
    inpatientMaxDays: '',
    priority: 100,
    coverageCode: '',
    message: '',
    note: '',
    effectiveFrom: '',
    effectiveTo: '',
    matchKeys: [
      { matchType: 'DX_EXACT', value1: '', value2: '', isExclude: false },
    ],
  }
}

function toIntOrNull(v) {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? Math.trunc(n) : null
}

export default function Uw3nnExceptionRulesAdminPage() {
  const { show } = useToast()
  const [viewMode, setViewMode] = useState('insurer') // 'insurer' | 'diagnosis'
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [items, setItems] = useState([])
  const [page, setPage] = useState(0)
  const [size] = useState(50)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)

  const [insurersLoading, setInsurersLoading] = useState(false)
  const [insurers, setInsurers] = useState([]) // {code,name}[]

  // filters
  const [asOf, setAsOf] = useState('')
  const [diagnosisCode, setDiagnosisCode] = useState('')
  const [diagnosisName, setDiagnosisName] = useState('')
  const [includeInactiveInsurers, setIncludeInactiveInsurers] = useState(false)
  const [selectedInsurerCodes, setSelectedInsurerCodes] = useState(() => new Set())
  const [reloadKey, setReloadKey] = useState(0)

  // diagnosis view
  const [diagnosisQuery, setDiagnosisQuery] = useState('')
  const [dxResult, setDxResult] = useState({ diagnosisCode: null, items: [] }) // grouped by insurer
  const [dxExpandedInsurers, setDxExpandedInsurers] = useState(() => new Set())

  // modal
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(emptyRule)

  const insurerList = useMemo(() => {
    const arr = Array.isArray(insurers) ? insurers : []
    return arr
      .map(x => ({ code: normalizeCode(x?.code), name: x?.name || x?.code }))
      .filter(x => x.code)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [insurers])

  const selectedCodesArray = useMemo(() => Array.from(selectedInsurerCodes), [selectedInsurerCodes])

  useEffect(() => {
    let ignore = false
    async function loadInsurers() {
      setInsurersLoading(true)
      try {
        const res = await api.get('/insurers')
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const list = Array.isArray(payload) ? payload : payload?.items || []
        if (!ignore) setInsurers(Array.isArray(list) ? list : [])
      } catch {
        if (!ignore) setInsurers([])
      } finally {
        if (!ignore) setInsurersLoading(false)
      }
    }
    loadInsurers()
    return () => { ignore = true }
  }, [])

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const params = {}
        if (asOf) params.asOf = asOf
        if (viewMode === 'insurer') {
          if (diagnosisCode) params.diagnosisCode = diagnosisCode
          if (diagnosisName) params.diagnosisName = diagnosisName
        }
        if (includeInactiveInsurers) params.includeInactiveInsurers = true
        if (selectedCodesArray.length > 0) params.insurerCodes = selectedCodesArray
        params.page = page
        params.size = size
        // 리스트는 경량화: matchKeys는 단건 조회/진단 필터에서만 로드
        if (viewMode === 'insurer') {
          const res = await api.get('/operator/uw3nn/exception-rules', { params })
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          const list = payload?.items || []
          if (!ignore) {
            setItems(Array.isArray(list) ? list : [])
            setTotalPages(Number(payload?.totalPages ?? 0) || 0)
            setTotalElements(Number(payload?.totalElements ?? (Array.isArray(list) ? list.length : 0)) || 0)
          }
        }
      } catch (e) {
        if (!ignore) setError('예외질환 룰을 불러오지 못했습니다')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, page, size])

  const loadByDiagnosis = async () => {
    const q = (diagnosisQuery || '').toString().trim()
    if (!q) {
      show('진단코드를 입력하세요 (예: KCD_B02, B02, K29.7)', 'error')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const params = {}
      if (asOf) params.asOf = asOf
      params.diagnosisCode = q
      if (includeInactiveInsurers) params.includeInactiveInsurers = true
      if (selectedCodesArray.length > 0) params.insurerCodes = selectedCodesArray
      const res = await api.get('/operator/uw3nn/exception-rules/by-diagnosis', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const list = payload?.items || []
      const arr = Array.isArray(list) ? list : []
      setDxResult({ diagnosisCode: payload?.diagnosisCode || null, items: arr })
      // 기본: 상위 몇 개만 자동 펼치기(버벅임 방지)
      setDxExpandedInsurers(new Set(arr.slice(0, 3).map(x => normalizeCode(x?.insurerCode)).filter(Boolean)))
    } catch (e) {
      setError('진단코드 기준 조회에 실패했습니다')
    } finally {
      setLoading(false)
    }
  }

  const openCreate = () => {
    setEditing(emptyRule())
    setModalOpen(true)
  }

  const openEdit = async (row) => {
    const id = row?.id
    if (!id) return
    setSaving(true)
    try {
      const res = await api.get(`/operator/uw3nn/exception-rules/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const item = payload?.item || row
      const mk = Array.isArray(item?.matchKeys) ? item.matchKeys : []
      setEditing({
        id: item?.id ?? null,
        insurerCode: normalizeCode(item?.insurerCode),
        templateId: item?.templateId != null ? String(item.templateId) : '',
        conditionKey: item?.conditionKey || '',
        decisionType: normalizeCode(item?.decisionType) || 'OK',
        waitingDays: item?.waitingDays ?? '',
        maxCount: item?.maxCount ?? '',
        inpatientMaxDays: item?.inpatientMaxDays ?? '',
        priority: item?.priority ?? 100,
        coverageCode: item?.coverageCode || '',
        message: item?.message || '',
        note: item?.note || '',
        effectiveFrom: item?.effectiveFrom || '',
        effectiveTo: item?.effectiveTo || '',
        matchKeys: mk.length > 0 ? mk.map(x => ({
          id: x?.id ?? null,
          matchType: normalizeCode(x?.matchType) || 'DX_EXACT',
          value1: x?.value1 || '',
          value2: x?.value2 || '',
          isExclude: !!x?.isExclude,
        })) : [{ matchType: 'DX_EXACT', value1: '', value2: '', isExclude: false }],
      })
      setModalOpen(true)
    } catch {
      show('룰 상세를 불러오지 못했습니다', 'error')
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async (row) => {
    const id = row?.id
    if (!id) return
    if (!window.confirm(`룰 #${id} 를 삭제하시겠습니까?`)) return
    try {
      await api.delete(`/operator/uw3nn/exception-rules/${id}`)
      show('삭제되었습니다', 'success')
      setItems(prev => (Array.isArray(prev) ? prev.filter(x => x.id !== id) : []))
    } catch (e) {
      show('삭제 실패', 'error')
    }
  }

  const onSave = async (e) => {
    e.preventDefault()
    const body = {
      insurerCode: normalizeCode(editing.insurerCode),
      templateId: toIntOrNull(editing.templateId),
      conditionKey: editing.conditionKey || null,
      decisionType: normalizeCode(editing.decisionType) || 'OK',
      waitingDays: toIntOrNull(editing.waitingDays),
      maxCount: toIntOrNull(editing.maxCount),
      inpatientMaxDays: toIntOrNull(editing.inpatientMaxDays),
      priority: toIntOrNull(editing.priority) ?? 100,
      coverageCode: (editing.coverageCode || '').toString().trim() || null,
      message: editing.message || null,
      note: editing.note || null,
      effectiveFrom: editing.effectiveFrom || null,
      effectiveTo: editing.effectiveTo || null,
      ruleVersion: null,
      matchKeys: (Array.isArray(editing.matchKeys) ? editing.matchKeys : []).map(mk => ({
        matchType: normalizeCode(mk.matchType),
        value1: mk.value1,
        value2: mk.value2 || null,
        isExclude: !!mk.isExclude,
      })),
    }
    if (!body.insurerCode) {
      show('보험사를 선택하세요', 'error')
      return
    }
    const hasAnyKey = Array.isArray(body.matchKeys) && body.matchKeys.some(k => k?.matchType && (k?.value1 || '').toString().trim())
    if (!hasAnyKey) {
      show('매칭키를 최소 1개 입력하세요', 'error')
      return
    }

    setSaving(true)
    try {
      const id = editing.id
      const res = id
        ? await api.put(`/operator/uw3nn/exception-rules/${id}`, body)
        : await api.post('/operator/uw3nn/exception-rules', body)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const item = payload?.item
      show('저장되었습니다', 'success')
      setModalOpen(false)
      setEditing(emptyRule())
      // refresh list
      setReloadKey(k => k + 1)
      return item
    } catch (err) {
      show('저장 실패: ' + (err?.response?.data?.message || err.message || ''), 'error')
    } finally {
      setSaving(false)
    }
  }

  const toggleInsurer = (code) => {
    const c = normalizeCode(code)
    if (!c) return
    setSelectedInsurerCodes(prev => {
      const next = new Set(prev)
      if (next.has(c)) next.delete(c)
      else next.add(c)
      return next
    })
  }

  const clearInsurers = () => setSelectedInsurerCodes(new Set())

  if (loading && (!items || items.length === 0)) return <Loading />
  if (error) return <InlineError message={error} />

  const canPrev = page > 0
  const canNext = totalPages ? page < (totalPages - 1) : (Array.isArray(items) && items.length >= size)

  return (
    <div className="p-4">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold">예외질환 관리</div>
          <div className="text-sm text-gray-600">운영자(OPERATOR) 전용. 예외질환 룰을 조회/추가/수정/삭제합니다.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
            onClick={() => { setPage(0); setReloadKey(k => k + 1) }}
            disabled={loading}
          >
            {loading ? '조회 중…' : '조회'}
          </button>
          <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm hover:opacity-90" onClick={openCreate}>
            + 추가
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={`rounded-full border px-3 py-1 text-sm ${viewMode === 'insurer' ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          onClick={() => { setViewMode('insurer'); setDxResult({ diagnosisCode: null, items: [] }); setPage(0); setReloadKey(k => k + 1) }}
        >
          보험사 기준
        </button>
        <button
          type="button"
          className={`rounded-full border px-3 py-1 text-sm ${viewMode === 'diagnosis' ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          onClick={() => { setViewMode('diagnosis'); setDxResult({ diagnosisCode: null, items: [] }) }}
        >
          진단코드 기준
        </button>
      </div>

      <div className="rounded border bg-white p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <label className="block text-xs text-slate-500">기준일(asOf)</label>
            <input className="mt-1 w-full rounded border px-3 py-2 text-sm" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </div>
          {viewMode === 'insurer' ? (
            <>
              <div>
                <label className="block text-xs text-slate-500">진료코드</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" placeholder="예: K29.7" value={diagnosisCode} onChange={(e) => setDiagnosisCode(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">진단명(키워드)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" placeholder="예: 위염" value={diagnosisName} onChange={(e) => setDiagnosisName(e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div className="md:col-span-2">
                <label className="block text-xs text-slate-500">진단코드 *</label>
                <div className="mt-1 flex gap-2">
                  <input
                    className="w-full rounded border px-3 py-2 text-sm"
                    placeholder="예: KCD_B02, B02, K29.7"
                    value={diagnosisQuery}
                    onChange={(e) => setDiagnosisQuery(e.target.value)}
                  />
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50 whitespace-nowrap"
                    onClick={loadByDiagnosis}
                    disabled={loading}
                  >
                    {loading ? '조회 중…' : '조회'}
                  </button>
                </div>
              </div>
            </>
          )}
          <div className="flex items-end gap-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={includeInactiveInsurers} onChange={(e) => setIncludeInactiveInsurers(e.target.checked)} />
              비활성 보험사 포함
            </label>
          </div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">보험사 필터</div>
            <button type="button" className="text-xs text-slate-600 hover:underline" onClick={clearInsurers}>선택 해제</button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {insurersLoading && <span className="text-xs text-slate-500">보험사 로딩 중…</span>}
            {!insurersLoading && insurerList.length === 0 && <span className="text-xs text-slate-500">보험사 목록이 없습니다</span>}
            {insurerList.slice(0, 60).map((i) => {
              const on = selectedInsurerCodes.has(i.code)
              return (
                <button
                  key={i.code}
                  type="button"
                  className={`rounded-full border px-3 py-1 text-xs ${on ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => toggleInsurer(i.code)}
                >
                  {i.name}
                </button>
              )
            })}
          </div>
          {insurerList.length > 60 && (
            <div className="mt-2 text-xs text-slate-400">보험사가 많아 상위 60개만 표시합니다(필터는 코드 입력 방식으로 확장 가능)</div>
          )}
        </div>
      </div>

      {viewMode === 'insurer' ? (
        <>
          <div className="overflow-x-auto rounded border bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-3 py-2">보험사</th>
                  <th className="px-3 py-2">커버리지</th>
                  <th className="px-3 py-2">결정</th>
                  <th className="px-3 py-2">조건키</th>
                  <th className="px-3 py-2">메시지</th>
                  <th className="px-3 py-2">유효기간</th>
                  <th className="px-3 py-2">매칭키</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(items) && items.length === 0 && (
                  <tr>
                    <td className="px-3 py-4 text-center text-slate-500" colSpan={8}>데이터가 없습니다</td>
                  </tr>
                )}
                {(Array.isArray(items) ? items : []).map((r) => {
                  const keys = Array.isArray(r.matchKeys) ? r.matchKeys : []
                  const keyCount = (r.matchKeyCount != null) ? Number(r.matchKeyCount) : (keys.length || 0)
                  return (
                    <tr key={r.id} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-medium">{r.insurerName || r.insurerCode}</div>
                        <div className="text-xs text-slate-500">{r.insurerCode}</div>
                        <div className="text-xs text-slate-400">#{r.id}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-mono text-xs">{r.coverageCode || '-'}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.decisionType}</td>
                      <td className="px-3 py-2">{r.conditionKey || '-'}</td>
                      <td className="px-3 py-2">{r.message || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs">{r.effectiveFrom || '-'} ~ {r.effectiveTo || '-'}</div>
                      </td>
                      <td className="px-3 py-2">
                        {keyCount <= 0 && <span className="text-xs text-slate-400">-</span>}
                        {keyCount > 0 && (
                          <div className="text-xs text-slate-600">총 {keyCount}개</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button className="text-brand-navy hover:underline mr-3" onClick={() => openEdit(r)}>수정</button>
                        <button className="text-red-600 hover:underline" onClick={() => onDelete(r)}>삭제</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
            <div>
              총 {totalElements || (Array.isArray(items) ? items.length : 0)}건
              {totalPages > 0 && <span className="ml-2 text-slate-400">({page + 1}/{totalPages} 페이지)</span>}
            </div>
            <div className="flex gap-2">
              <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={!canPrev || loading} onClick={() => setPage(p => Math.max(0, p - 1))}>이전</button>
              <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={!canNext || loading} onClick={() => setPage(p => p + 1)}>다음</button>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {dxResult?.diagnosisCode && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => setDxExpandedInsurers(new Set((Array.isArray(dxResult?.items) ? dxResult.items : []).map(x => normalizeCode(x?.insurerCode)).filter(Boolean)))}
              >
                모두 펼치기
              </button>
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => setDxExpandedInsurers(new Set())}
              >
                모두 접기
              </button>
            </div>
          )}
          <div className="text-sm text-slate-600">
            {dxResult?.diagnosisCode ? (
              <span>조회 코드: <span className="font-mono font-semibold">{dxResult.diagnosisCode}</span></span>
            ) : (
              <span className="text-slate-400">진단코드를 입력하고 조회를 누르세요.</span>
            )}
          </div>

          {(Array.isArray(dxResult?.items) ? dxResult.items : []).length === 0 && dxResult?.diagnosisCode && (
            <div className="rounded border bg-white p-4 text-sm text-slate-500">매칭되는 룰이 없습니다.</div>
          )}

          {(Array.isArray(dxResult?.items) ? dxResult.items : []).map((ins) => {
            const code = normalizeCode(ins?.insurerCode)
            const open = dxExpandedInsurers.has(code)
            return (
            <details
              key={ins.insurerCode}
              className="rounded border bg-white"
              open={open}
              onToggle={(e) => {
                const isOpen = !!e?.currentTarget?.open
                setDxExpandedInsurers(prev => {
                  const next = new Set(prev)
                  if (!code) return next
                  if (isOpen) next.add(code)
                  else next.delete(code)
                  return next
                })
              }}
            >
              <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{ins.insurerName || ins.insurerCode}</div>
                  <div className="text-xs text-slate-500">{ins.insurerCode} · 룰 {ins.ruleCount || (ins.rules?.length || 0)}개</div>
                </div>
                <span className="text-xs text-slate-400">펼치기</span>
              </summary>
              <div className="border-t p-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-3 py-2">룰</th>
                      <th className="px-3 py-2">커버리지</th>
                      <th className="px-3 py-2">결정</th>
                      <th className="px-3 py-2">대기일</th>
                      <th className="px-3 py-2">횟수</th>
                      <th className="px-3 py-2">입원최대일</th>
                      <th className="px-3 py-2">우선순위</th>
                      <th className="px-3 py-2">메시지</th>
                      <th className="px-3 py-2">비고</th>
                      <th className="px-3 py-2">유효기간</th>
                      <th className="px-3 py-2">매칭키(해당 코드에 hit)</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(ins.rules) ? ins.rules : []).map((r) => (
                      <tr key={r.ruleId} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="font-medium">#{r.ruleId}</div>
                          {r.templateCode && <div className="text-xs text-slate-500">template: {r.templateCode}</div>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="font-mono text-xs">{r.coverageCode || '-'}</span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.decisionType}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.waitingDays ?? '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.maxCount ?? '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.inpatientMaxDays ?? '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.priority ?? '-'}</td>
                        <td className="px-3 py-2">{r.message || '-'}</td>
                        <td className="px-3 py-2">{r.note || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-xs">{r.effectiveFrom || '-'} ~ {r.effectiveTo || '-'}</div>
                        </td>
                        <td className="px-3 py-2">
                          {(Array.isArray(r.matchedKeys) ? r.matchedKeys : []).map((k, idx) => (
                            <div key={idx} className="text-xs">
                              <span className="font-mono">{k.matchType}</span> {k.value1}{k.value2 ? `~${k.value2}` : ''}
                            </div>
                          ))}
                          {(Array.isArray(r.matchedKeys) ? r.matchedKeys : []).length === 0 && (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button className="text-brand-navy hover:underline mr-3" onClick={() => openEdit({ id: r.ruleId })}>수정</button>
                          <button className="text-red-600 hover:underline" onClick={() => onDelete({ id: r.ruleId })}>삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )})}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
          <form onSubmit={onSave} className="w-full max-w-3xl rounded bg-white p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-semibold">{editing.id ? `룰 수정 #${editing.id}` : '룰 추가'}</div>
                <div className="text-xs text-slate-500">DX_* 는 코드 입력 시 자동으로 점/공백 제거 + 대문자로 저장됩니다.</div>
              </div>
              <button type="button" className="text-sm text-slate-600 hover:underline" onClick={() => { if (!saving) { setModalOpen(false); setEditing(emptyRule()) } }}>
                닫기
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-500">보험사 *</label>
                <select className="mt-1 w-full rounded border px-3 py-2 text-sm" value={editing.insurerCode} onChange={(e) => setEditing(p => ({ ...p, insurerCode: e.target.value }))}>
                  <option value="">선택</option>
                  {insurerList.map(i => <option key={i.code} value={i.code}>{i.name} ({i.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500">템플릿 ID(선택)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" placeholder="예: 123" value={editing.templateId} onChange={(e) => setEditing(p => ({ ...p, templateId: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">결정(안전모드)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm bg-slate-50" value="OK" readOnly />
                <div className="mt-1 text-[11px] text-slate-500">오해 방지를 위해 운영자 관리 화면에서는 전부 OK로 저장/표기합니다.</div>
              </div>
              <div>
                <label className="block text-xs text-slate-500">대기일(waitingDays)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={editing.waitingDays} onChange={(e) => setEditing(p => ({ ...p, waitingDays: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">횟수(maxCount)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={editing.maxCount} onChange={(e) => setEditing(p => ({ ...p, maxCount: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">입원 최대일(inpatientMaxDays)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={editing.inpatientMaxDays} onChange={(e) => setEditing(p => ({ ...p, inpatientMaxDays: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">우선순위(priority)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={editing.priority} onChange={(e) => setEditing(p => ({ ...p, priority: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">커버리지 코드(coverageCode)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" placeholder="예: death, ci2, cancer" value={editing.coverageCode} onChange={(e) => setEditing(p => ({ ...p, coverageCode: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">유효 시작(effectiveFrom)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" type="date" value={editing.effectiveFrom} onChange={(e) => setEditing(p => ({ ...p, effectiveFrom: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-slate-500">유효 종료(effectiveTo)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" type="date" value={editing.effectiveTo} onChange={(e) => setEditing(p => ({ ...p, effectiveTo: e.target.value }))} />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs text-slate-500">조건키(conditionKey)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={editing.conditionKey} onChange={(e) => setEditing(p => ({ ...p, conditionKey: e.target.value }))} />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs text-slate-500">메시지(message)</label>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={editing.message} onChange={(e) => setEditing(p => ({ ...p, message: e.target.value }))} />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs text-slate-500">비고(note)</label>
                <textarea className="mt-1 w-full rounded border px-3 py-2 text-sm" rows={2} value={editing.note} onChange={(e) => setEditing(p => ({ ...p, note: e.target.value }))} />
              </div>
            </div>

            <div className="rounded border bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">매칭키</div>
                <button
                  type="button"
                  className="rounded border bg-white px-3 py-1 text-xs hover:bg-slate-50"
                  onClick={() => setEditing(p => ({ ...p, matchKeys: [...(p.matchKeys || []), { matchType: 'DX_EXACT', value1: '', value2: '', isExclude: false }] }))}
                >
                  + 키 추가
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {(Array.isArray(editing.matchKeys) ? editing.matchKeys : []).map((mk, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-3">
                      <label className="block text-xs text-slate-500">matchType</label>
                      <select className="mt-1 w-full rounded border px-3 py-2 text-sm" value={mk.matchType} onChange={(e) => {
                        const v = e.target.value
                        setEditing(p => {
                          const next = [...(p.matchKeys || [])]
                          next[idx] = { ...(next[idx] || {}), matchType: v }
                          return { ...p, matchKeys: next }
                        })
                      }}>
                        {MATCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-4">
                      <label className="block text-xs text-slate-500">value1</label>
                      <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={mk.value1} onChange={(e) => {
                        const v = e.target.value
                        setEditing(p => {
                          const next = [...(p.matchKeys || [])]
                          next[idx] = { ...(next[idx] || {}), value1: v }
                          return { ...p, matchKeys: next }
                        })
                      }} />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs text-slate-500">value2(선택)</label>
                      <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={mk.value2 || ''} onChange={(e) => {
                        const v = e.target.value
                        setEditing(p => {
                          const next = [...(p.matchKeys || [])]
                          next[idx] = { ...(next[idx] || {}), value2: v }
                          return { ...p, matchKeys: next }
                        })
                      }} />
                    </div>
                    <div className="md:col-span-1">
                      <label className="block text-xs text-slate-500">exclude</label>
                      <div className="mt-2">
                        <input type="checkbox" checked={!!mk.isExclude} onChange={(e) => {
                          const v = e.target.checked
                          setEditing(p => {
                            const next = [...(p.matchKeys || [])]
                            next[idx] = { ...(next[idx] || {}), isExclude: v }
                            return { ...p, matchKeys: next }
                          })
                        }} />
                      </div>
                    </div>
                    <div className="md:col-span-1 text-right">
                      <button type="button" className="text-red-600 text-xs hover:underline" onClick={() => {
                        setEditing(p => {
                          const next = [...(p.matchKeys || [])]
                          next.splice(idx, 1)
                          return { ...p, matchKeys: next.length > 0 ? next : [{ matchType: 'DX_EXACT', value1: '', value2: '', isExclude: false }] }
                        })
                      }}>
                        삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" disabled={saving} onClick={() => { setModalOpen(false); setEditing(emptyRule()) }}>
                취소
              </button>
              <button type="submit" className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-60" disabled={saving}>
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

